import { CATALOGO_ONBOARDING } from '@core/onboarding/onboarding.catalogo';
import { OnboardingService } from '@core/onboarding/onboarding.service';
import { mesmoFluxoDeOrientacao } from '@core/onboarding/onboarding.telas';
import { DOCUMENT } from '@angular/common';
import { ComponentRef, Injectable, NgZone, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PassoComponent } from './passo.component';
import { RecorteComponent } from './recorte.component';
import { PassoDoTutorial, PosicaoDoPasso, Tutorial } from './tutorial.model';
import { casaComParametro, tourDaRota, tourPorId, TOURS } from './tours';

/** Onde ficam os tours já concluídos (ou pulados) deste navegador. */
export const CHAVE_DE_VISTOS = 'mg.tutorial.vistos';

/** Pequena pausa para a navegação; a chegada dos campos é observada separadamente. */
export const ATRASO_DE_DISPARO_MS = 800;
const ESPERA_DA_TELA_MS = 15000;
const ESPERA_DA_ABA_MS = 2000;

/** Distância entre a borda do alvo e o balão. */
const AFASTAMENTO = 12;

const POSICOES: Readonly<Record<PosicaoDoPasso, ConnectedPosition>> = {
  abaixo: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: AFASTAMENTO },
  acima: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -AFASTAMENTO },
  direita: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: AFASTAMENTO },
  esquerda: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -AFASTAMENTO },
};

const ORDEM_PADRAO: readonly PosicaoDoPasso[] = ['abaixo', 'acima', 'direita', 'esquerda'];

/**
 * O motor do tutorial guiado.
 *
 * ## O que ele é
 *
 * Uma máquina de passos (em signals) mais a montagem do overlay do CDK. Ele é
 * `providedIn: 'root'` e vive a sessão inteira: é ele quem observa a rota, e é
 * a barra superior que o injeta — a construção acontece quando a casca
 * renderiza.
 *
 * ## Por que não uma biblioteca de mercado
 *
 * Porque o que as bibliotecas de tour entregam de valor é o posicionamento, e
 * isso o `FlexibleConnectedPositionStrategy` do CDK — que este produto já
 * carrega, por causa de todo `mat-menu` e `mat-tooltip` da tela — faz melhor e
 * sem um byte a mais no bundle. O que elas NÃO entregam é o que quebra tour na
 * prática: a garantia de que o alvo declarado ainda existe no template. Essa
 * garantia é o portão `src/testing/__arquitetura__/tutorial-alvos.pure.spec.ts`,
 * e ela só é possível porque o alvo é um atributo nosso, não um seletor CSS
 * escrito numa string de configuração de terceiro.
 *
 * Tours automáticos acompanham somente a tarefa aberta pelo roteiro.
 * Ajuda e suporte permite abrir o tour da tela por iniciativa do usuário,
 * independentemente da preferência ou conclusão do onboarding. localStorage guarda apenas vistos,
 * nunca comprova aprendizagem nem libera a Órbita.
 */
@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly onboarding = inject(OnboardingService);
  private readonly overlay = inject(Overlay);
  private readonly documento = inject(DOCUMENT);
  private readonly anunciador = inject(LiveAnnouncer);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private readonly tourAtivo = signal<Tutorial | null>(null);
  private readonly tourDoDialogo = signal<Tutorial | null>(null);
  private readonly indiceAtual = signal(0);

  /** O tour em execução, ou `null` quando não há tour aberto. */
  readonly tour: Signal<Tutorial | null> = this.tourAtivo.asReadonly();

  /** O passo corrente, base 0. */
  readonly indice: Signal<number> = this.indiceAtual.asReadonly();

  readonly total: Signal<number> = computed(() => this.tourAtivo()?.passos.length ?? 0);

  readonly passoAtual: Signal<PassoDoTutorial | null> = computed(
    () => this.tourAtivo()?.passos[this.indiceAtual()] ?? null
  );

  private readonly encerradoSubject = new Subject<string>();

  /**
   * Emite o id do tour que acabou de ser CONCLUÍDO, PULADO ou fechado por Esc —
   * os três passam por `encerrar()`. O fechamento por "nenhum alvo na tela"
   * (`fechar()`) NÃO emite: ali o tour não aconteceu. Quem assina é a Órbita
   * Financeira, para abrir depois do tour do painel (spec 2026-09-07, §6.3).
   */
  readonly encerrado$ = this.encerradoSubject.asObservable();

  private balao?: OverlayRef;
  private recorte?: OverlayRef;
  private componenteBalao?: ComponentRef<PassoComponent>;
  private componenteLuz?: ComponentRef<RecorteComponent>;
  private posicionamento?: FlexibleConnectedPositionStrategy;
  private pararObservacaoDoDialogo?: () => void;
  private alvoDoDialogo?: HTMLElement;
  private focoSolicitado?: Element | null;
  private disparoAgendado?: ReturnType<typeof setTimeout>;
  private readonly acompanhamento = signal<{ rota: string; capitulo: string; contexto: string } | null>(null);
  private acaoSolicitada: string | null = null;
  private dialogoDispensado = false;
  private readonly dialogosAcompanhados: { tour: Tutorial; dispensado: boolean }[] = [];
  private readonly apresentadosNaTarefa = new Set<string>();
  private geracao = 0;
  private cancelarEspera?: () => void;
  private inicioManual = false;

  /** A preferência ativa sozinha não inicia uma tarefa nem libera seus guias. */
  readonly telaAssistida = computed(() => {
    const tarefa = this.acompanhamento();
    return !!tarefa && this.onboarding.emImplantacao()
      && this.onboarding.estado()?.guia === 'guiado'
      && tarefa.contexto === this.onboarding.contexto()
      && tarefa.capitulo === this.onboarding.estado()?.capitulo
      && mesmoFluxoDeOrientacao(tarefa.rota, this.rotaAtual());
  });

  /**
   * A rota corrente é SIGNAL, e não campo comum, porque quem a lê é o
   * `[disabled]` do "?" na barra superior — e a barra é `OnPush`. Campo comum
   * mudava em silêncio: o Angular não tem como saber que o valor de
   * `temTourNaRota()` envelheceu, e a navegação pelo menu lateral não marca a
   * view do cabeçalho (o motivo da mudança nasce AQUI, no serviço, não num
   * evento do template dele). O botão ficava travado ao entrar no painel vindo
   * de uma tela sem tour, e liberado no caminho inverso. Lido dentro de um
   * signal, o binding assina a mudança e a view se marca sozinha.
   */
  private readonly rotaAtual = signal(this.router.url);

  constructor() {
    this.router.events.pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd)).subscribe(evento => {
      const tourManual = this.inicioManual && this.tourAtivo();
      if (tourManual && this.caminho(this.rotaAtual()) === this.caminho(evento.urlAfterRedirects)) {
        const alvoAnterior = this.passoAtual()?.alvo;
        this.rotaAtual.set(evento.urlAfterRedirects);
        const atualizado = this.tourDaTela();
        // Filtros e escolhas do formulário alteram a query sem sair da tela.
        // Uma mudança de tipo pode selecionar uma orientação mais específica.
        if (atualizado && atualizado !== tourManual) {
          const disponivel = atualizado.passos.find(passo => passo.alvo === alvoAnterior && this.elementoDoAlvo(passo.alvo))
            ?? atualizado.passos.find(passo => this.elementoDoAlvo(passo.alvo));
          this.abrirTour(atualizado.id, disponivel?.alvo, false, true);
        } else if (!atualizado) this.fechar();
        return;
      }
      this.fechar();
      this.tourDoDialogo.set(null);
      this.dialogosAcompanhados.length = 0;
      this.rotaAtual.set(evento.urlAfterRedirects);
      const tarefa = this.acompanhamento();
      if (tarefa && !mesmoFluxoDeOrientacao(tarefa.rota, evento.urlAfterRedirects)) {
        this.cancelarAcompanhamento();
      }
      if (this.acaoSolicitada !== this.caminho(evento.urlAfterRedirects)) this.acaoSolicitada = null;
      this.agendarDisparoAutomatico();
    });

    let contexto = this.onboarding.contexto();
    effect(() => {
      const emImplantacao = this.onboarding.emImplantacao();
      const atual = this.onboarding.contexto();
      const estado = this.onboarding.estado();
      untracked(() => {
        const tarefa = this.acompanhamento();
        if (atual !== contexto) {
          this.cancelarAcompanhamento();
        } else if (tarefa && (!emImplantacao || estado?.guia !== 'guiado' || tarefa.capitulo !== estado?.capitulo)) {
          this.cancelarAcompanhamento(this.inicioManual);
        }
        contexto = atual;
        this.agendarDisparoAutomatico();
      });
    });
  }

  /** Chamado somente ao abrir uma ação do capítulo atual em Primeiros passos. */
  acompanharAcao(rota: string): void {
    this.cancelarAcompanhamento();
    const capitulo = CATALOGO_ONBOARDING.find(item => item.id === this.onboarding.estado()?.capitulo);
    if (this.caminho(this.router.url) !== '/painel/primeiros-passos'
      || !this.onboarding.emImplantacao() || this.onboarding.estado()?.guia !== 'guiado'
      || !capitulo?.acoes.some(acao => acao.rota === rota)) return;
    this.acompanhamento.set({ rota, capitulo: capitulo.id, contexto: this.onboarding.contexto() });
    this.acaoSolicitada = this.caminho(rota);
  }

  cancelarAcompanhamento(preservarTourManual = false): void {
    this.acompanhamento.set(null);
    this.acaoSolicitada = null;
    this.apresentadosNaTarefa.clear();
    if (preservarTourManual) return;
    this.tourDoDialogo.set(null);
    this.dialogoDispensado = false;
    this.dialogosAcompanhados.length = 0;
    this.fechar();
  }

  temTourNaRota(): boolean {
    return this.tourDaTela() !== null;
  }

  iniciarTourDaRota(focarGuia = true): void {
    const tour = this.tourDaTela();
    if (tour) this.abrirTour(tour.id, undefined, focarGuia, true);
  }

  /** O cadastro aberto substitui o guia da lista até ser fechado. */
  acompanharDialogo(idDoTour: string): () => void {
    const base = tourPorId(idDoTour);
    if (!base?.dialogo || !casaComParametro(this.caminho(this.rotaAtual()), base.rota)) return () => undefined;
    // Uma variante do diálogo, própria do parâmetro da URL (ex.: revisão fiscal), substitui o guia genérico.
    const parametros = new URLSearchParams(this.rotaAtual().split('?')[1]?.split('#')[0] ?? '');
    const tour = TOURS.find(item => item.dialogo && item.rota === base.rota && !!item.parametros
      && Object.entries(item.parametros).every(([chave, valor]) => parametros.get(chave) === valor)
      && item.passos.some(passo => passo.alvo.startsWith(idDoTour + '.'))) ?? base;
    const anterior = this.dialogosAcompanhados.at(-1);
    if (anterior) anterior.dispensado = this.dialogoDispensado;
    const acompanhamento = { tour, dispensado: false };
    this.dialogosAcompanhados.push(acompanhamento);
    const continuarManual = this.inicioManual;
    this.fechar();
    this.tourDoDialogo.set(tour);
    this.dialogoDispensado = false;
    this.acaoSolicitada = this.caminho(this.rotaAtual());
    if (continuarManual) this.abrirTour(tour.id, undefined, false, true);
    else this.agendarDisparoAutomatico();
    return () => {
      const indice = this.dialogosAcompanhados.indexOf(acompanhamento);
      if (indice < 0) return;
      const eraAtual = indice === this.dialogosAcompanhados.length - 1;
      this.dialogosAcompanhados.splice(indice, 1);
      if (!eraAtual) return;
      this.fechar();
      const retomado = this.dialogosAcompanhados.at(-1);
      this.tourDoDialogo.set(retomado?.tour ?? null);
      this.dialogoDispensado = retomado?.dispensado ?? false;
      this.acaoSolicitada = null;
      // Voltar na pilha restaura o guia disponível, sem reabrir uma explicação encerrada.
    };
  }

  /** Aguarda o select fechar e os campos condicionais aparecerem. Não preenche o formulário. */
  orientarDialogo(alvo: string): void {
    const tour = this.tourDoDialogo();
    if (!this.podeApresentarTour() || !tour || this.dialogoDispensado) return;
    clearTimeout(this.disparoAgendado);
    this.disparoAgendado = setTimeout(() => {
      this.disparoAgendado = undefined;
      if (this.tourDoDialogo() !== tour || !this.podeApresentarTour() || this.dialogoDispensado) return;
      const atual = this.passoAtual();
      // Uma resposta assíncrona não deve pular a explicação que está sendo lida.
      if (this.tourAtivo() === tour && atual && this.elementoDoAlvo(atual.alvo)) return;
      this.abrirTour(tour.id, alvo, false, this.inicioManual);
    }, ATRASO_DE_DISPARO_MS);
  }

  private tourDaTela(): Tutorial | null {
    return this.tourDoDialogo() ?? tourDaRota(this.rotaAtual());
  }

  iniciar(idDoTour: string, alvoInicial?: string, focarGuia = false): void {
    this.abrirTour(idDoTour, alvoInicial, focarGuia, false);
  }

  private podeApresentarTour(): boolean {
    return this.inicioManual || this.telaAssistida();
  }

  private abrirTour(idDoTour: string, alvoInicial: string | undefined, focarGuia: boolean, manual: boolean): void {
    if (!manual && !this.telaAssistida()) return;
    const tour = tourPorId(idDoTour);
    if (!tour || tour !== this.tourDaTela() || tour.passos.length === 0) return;
    const inicio = alvoInicial ? tour.passos.findIndex(passo => passo.alvo === alvoInicial) : 0;
    if (inicio < 0) return;
    if (tour.dialogo) this.dialogoDispensado = false;

    // Uma ação explícita pode rever um tour já visto. Tours de cadastro nunca
    // comprovam registros salvos; a visita ao dashboard é uma orientação.
    this.acaoSolicitada = null;
    this.fechar();
    this.inicioManual = manual;
    if ((tour.dialogo || tour.interativo) && focarGuia) this.focoSolicitado = this.documento.activeElement;
    this.tourAtivo.set(tour);
    this.indiceAtual.set(inicio);
    void this.mover(inicio, 1, true);
  }

  proximo(): void {
    if (this.passosExibidos().at(-1) === this.passoAtual()) {
      this.encerrar(true);
      return;
    }

    void this.mover(this.indiceAtual() + 1, 1);
  }

  anterior(): void {
    if (this.indiceAtual() === 0) {
      return;
    }

    void this.mover(this.indiceAtual() - 1, -1);
  }

  /** Encerra esta apresentação. O acompanhamento permite reabrir explicitamente. */
  pular(): void {
    if (this.tourAtivo()?.dialogo) this.dialogoDispensado = true;
    this.encerrar();
  }

  /** Fecha o tour e registra que este usuário já o viu. */
  encerrar(concluido = false): void {
    const tour = this.tourAtivo();
    const tarefa = this.acompanhamento();
    const tourConcluiOrientacao = tour?.id === 'dashboard'
      || (tour?.id === 'conta-receber-recorrencia' && tarefa?.capitulo === 'recorrencia');
    const orientacao = concluido && tourConcluiOrientacao && this.telaAssistida()
      && CATALOGO_ONBOARDING.find(item => item.id === tarefa?.capitulo && item.orientacao);

    if (tour) {
      this.marcarComoVisto(tour.id);
    }

    this.fechar();

    if (orientacao) void this.onboarding.concluirOrientacao(orientacao.id);

    // DEPOIS de `fechar()`: quem assina lê `tour()` já nulo e `jaViu` já verdadeiro.
    if (tour) {
      this.encerradoSubject.next(tour.id);
    }
  }

  /** Fecha SEM registrar — ver o caso "nenhum alvo na tela" em `mover`. */
  private fechar(): void {
    ++this.geracao;
    clearTimeout(this.disparoAgendado);
    this.disparoAgendado = undefined;
    this.cancelarEspera?.();
    this.fecharOverlays();
    this.tourAtivo.set(null);
    this.inicioManual = false;
    this.indiceAtual.set(0);
  }

  /** Os ids já concluídos ou pulados neste navegador. */
  jaViu(idDoTour: string): boolean {
    return this.vistos().includes(idDoTour);
  }

  /**
   * Leitura do `localStorage`, à prova de navegador com dado de site bloqueado.
   *
   * O `try` cobre a LEITURA, e não só o `JSON.parse`: em navegador configurado
   * para bloquear armazenamento, o simples acesso a `localStorage` já lança
   * `SecurityError` — o erro chega antes de haver o que interpretar.
   */
  private vistos(): string[] {
    try {
      const bruto = localStorage.getItem(CHAVE_DE_VISTOS);
      const lido: unknown = bruto ? JSON.parse(bruto) : [];

      return Array.isArray(lido) ? lido.filter((item): item is string => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  private marcarComoVisto(idDoTour: string): void {
    const atualizado = [...new Set([...this.vistos(), idDoTour])];

    try {
      localStorage.setItem(CHAVE_DE_VISTOS, JSON.stringify(atualizado));
    } catch {
      // Sem armazenamento, o tour reabre na próxima visita. É degradação
      // aceitável: o produto continua inteiro, só insiste mais.
    }
  }

  private agendarDisparoAutomatico(): void {
    clearTimeout(this.disparoAgendado);
    if (!this.telaAssistida() || !this.temTourNaRota() || this.tourAtivo()) return;
    const tour = this.tourDaTela();
    if (!tour || (this.acaoSolicitada !== this.caminho(this.rotaAtual())
      && this.apresentadosNaTarefa.has(tour.id))) return;
    // Apresenta uma vez nesta tarefa. Sair do fluxo encerra o acompanhamento;
    // uma nova ação no roteiro permite rever os mesmos guias.
    this.zone.runOutsideAngular(() => {
      this.disparoAgendado = setTimeout(() => this.zone.run(() => this.iniciar(tour.id)), ATRASO_DE_DISPARO_MS);
    });
  }

  private async mover(inicio: number, direcao: 1 | -1, primeiraTentativa = false): Promise<void> {
    const tour = this.tourAtivo();
    if (!tour) return;
    const geracao = ++this.geracao;
    this.cancelarEspera?.();

    for (let posicao = inicio; posicao >= 0 && posicao < tour.passos.length; posicao += direcao) {
      const passo = tour.passos[posicao];
      let mudouDeAba = false;
      if (passo.aba) {
        const rotulo = this.elementoDoAlvo(passo.aba)
          ?? (primeiraTentativa && posicao === inicio ? await this.esperarAlvo(passo.aba, ESPERA_DA_TELA_MS) : null);
        if (geracao !== this.geracao || !this.podeApresentarTour()) return;
        const aba = rotulo?.closest<HTMLElement>('[role="tab"]');
        if (!aba) continue; // Aba opcional ou indisponível para a permissão atual.
        if (aba.getAttribute('aria-selected') !== 'true') {
          this.fecharOverlays();
          aba.click(); // Só troca abas; nunca preenche nem salva dados.
          mudouDeAba = true;
        }
      }
      if (passo.etapa) {
        const rotulo = this.elementoDoAlvo(passo.etapa)
          ?? (primeiraTentativa && posicao === inicio ? await this.esperarAlvo(passo.etapa, ESPERA_DA_TELA_MS) : null);
        if (geracao !== this.geracao || !this.podeApresentarTour()) return;
        const etapa = rotulo?.closest<HTMLElement>('mat-step-header[role="button"], mat-step-header[role="tab"]');
        if (!etapa) continue;
        if (etapa.getAttribute('aria-selected') !== 'true' && etapa.getAttribute('aria-expanded') !== 'true') {
          this.fecharOverlays();
          // O próprio stepper valida a navegação. aria-disabled também marca etapas
          // ainda não visitadas, mesmo quando a etapa atual contém só conferência.
          etapa.click();
          mudouDeAba = true;
        }
      }
      const espera = (primeiraTentativa && posicao === inicio) || passo.aguardarAlvo
        ? ESPERA_DA_TELA_MS : mudouDeAba ? ESPERA_DA_ABA_MS : 0;
      const alvo = this.elementoDoAlvo(passo.alvo) ?? (espera ? await this.esperarAlvo(passo.alvo, espera) : null)
        ?? (passo.alvoAlternativo ? this.elementoDoAlvo(passo.alvoAlternativo) : null);
      if (geracao !== this.geracao || !this.podeApresentarTour()) return;
      if (alvo) {
        this.apresentadosNaTarefa.add(tour.id);
        this.indiceAtual.set(posicao);
        this.mostrar(passo, alvo);
        return;
      }
    }
    if (direcao === -1) return;
    if (primeiraTentativa) this.fechar();
    else this.encerrar(true);
  }

  /** Espera campos assíncronos sem perder o tour por uma resposta HTTP mais lenta. */
  private esperarAlvo(alvo: string, limite: number): Promise<HTMLElement | null> {
    return this.zone.runOutsideAngular(() => new Promise(resolve => {
      const Observer = this.documento.defaultView?.MutationObserver;
      let observador: MutationObserver | undefined;
      const finalizar = (elemento: HTMLElement | null) => {
        clearTimeout(prazo);
        observador?.disconnect();
        this.documento.removeEventListener('transitionend', conferir, true);
        this.cancelarEspera = undefined;
        this.zone.run(() => resolve(elemento));
      };
      const conferir = () => {
        const elemento = this.elementoDoAlvo(alvo);
        if (elemento) finalizar(elemento);
      };
      const prazo = setTimeout(() => finalizar(this.elementoDoAlvo(alvo)), limite);
      this.cancelarEspera = () => finalizar(null);
      this.documento.addEventListener('transitionend', conferir, true);
      if (Observer) {
        observador = new Observer(conferir);
        observador.observe(this.documento.body, { childList: true, subtree: true, attributes: true });
      }
    }));
  }

  private elementoDoAlvo(alvo: string): HTMLElement | null {
    const visivel = (no: HTMLElement) => {
      const rect = no.getBoundingClientRect();
      const estilo = this.documento.defaultView?.getComputedStyle(no);
      return rect.width > 0 && rect.height > 0 && estilo?.visibility !== 'hidden';
    };
    for (const elemento of Array.from(this.documento.querySelectorAll<HTMLElement>(`[data-tour="${alvo}"]`))) {
      if (elemento.closest('[aria-hidden="true"], [inert], [hidden]')) continue;
      const painel = elemento.closest<HTMLElement>('[role="tabpanel"]');
      // Não medir a aba durante sua animação, nem selecionar o pai oculto de uma tela empilhada.
      if (painel?.getAnimations?.({ subtree: true }).some(animacao => animacao.playState === 'running'
        && 'transitionProperty' in animacao && animacao.transitionProperty === 'transform')) continue;
      if (visivel(elemento)) return elemento;
      // Hosts Angular inline podem não ter caixa própria.
      const filho = Array.from(elemento.children).find((no): no is HTMLElement => no instanceof HTMLElement && visivel(no));
      if (filho) return filho;
    }
    return null;
  }

  private caminho(rota: string): string {
    return rota.split(/[?#]/)[0].replace(/\/+$/, '');
  }

  private mostrar(passo: PassoDoTutorial, alvo: HTMLElement, rolar = true): void {
    const interativo = this.tourAtivo()?.dialogo === true || this.tourAtivo()?.interativo === true;
    this.pararObservacaoDoDialogo?.();
    this.pararObservacaoDoDialogo = undefined;
    // No formulário, manter o balão montado preserva o foco e seus controles.
    if (!interativo) this.fecharOverlays();
    this.alvoDoDialogo = interativo ? alvo : undefined;
    if (rolar) alvo.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'instant' });

    if (!this.recorte) {
      this.recorte = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.noop(),
        positionStrategy: this.overlay.position().global(),
        panelClass: 'mg-tutorial__luz',
      });
      this.componenteLuz = this.recorte.attach(new ComponentPortal(RecorteComponent));
    }
    this.componenteLuz?.instance.focar(alvo);

    if (!this.balao) {
      this.posicionamento = this.overlay.position().flexibleConnectedTo(alvo)
        .withPositions(this.posicoesPara(passo.posicao)).withFlexibleDimensions(false).withPush(true);
      this.balao = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        positionStrategy: this.posicionamento,
        panelClass: 'mg-tutorial__balao',
      });
      this.componenteBalao = this.balao.attach(new ComponentPortal(PassoComponent));
      this.componenteBalao.instance.pular.subscribe(() => this.pular());
      this.componenteBalao.instance.anterior.subscribe(() => this.anterior());
      this.componenteBalao.instance.proximo.subscribe(() => this.proximo());
      const aoEscape = (evento: KeyboardEvent) => {
        if (evento.key !== 'Escape') return;
        evento.preventDefault();
        evento.stopPropagation();
        this.pular();
      };
      if (interativo) {
        // Esc no campo/select pertence ao formulário, não ao balão não modal.
        this.balao.overlayElement.addEventListener('keydown', evento => {
          aoEscape(evento);
          if (evento.key !== 'Tab') return;
          const botoes = this.balao?.overlayElement.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
          const limite = evento.shiftKey ? botoes?.[0] : botoes?.[botoes.length - 1];
          if (limite && evento.target === limite) {
            evento.preventDefault();
            this.focarCampoDoDialogo();
          }
        });
      } else {
        this.balao.keydownEvents().subscribe(aoEscape);
      }
    } else {
      this.posicionamento?.setOrigin(alvo).withPositions(this.posicoesPara(passo.posicao));
    }
    const explicacao = !this.telaAssistida() && (passo.corpoManual || passo.tituloManual)
      ? { ...passo, corpo: passo.corpoManual ?? passo.corpo, titulo: passo.tituloManual ?? passo.titulo }
      : passo;
    this.componenteBalao?.setInput('interativo', interativo);
    this.componenteBalao?.setInput('passo', explicacao);
    this.atualizarContador();
    this.visibilidadeDoGuia(true);
    this.balao.updatePosition();
    if (interativo) this.observarDialogo(alvo);
    const passos = this.passosExibidos();
    void this.anunciador.announce(`${explicacao.titulo}. Passo ${passos.indexOf(passo) + 1} de ${passos.length}.`, 'polite');
  }

  private passosExibidos(): PassoDoTutorial[] {
    const tour = this.tourAtivo();
    return tour?.passos.filter(passo => !tour.dialogo
      || !!this.elementoDoAlvo(passo.alvo)
      || !!(passo.alvoAlternativo && this.elementoDoAlvo(passo.alvoAlternativo))
      || !!(passo.aba && this.elementoDoAlvo(passo.aba))
      || !!(passo.etapa && this.elementoDoAlvo(passo.etapa))) ?? [];
  }

  private atualizarContador(): void {
    const balao = this.componenteBalao;
    if (!balao) return;
    const passos = this.passosExibidos();
    const indice = Math.max(0, passos.indexOf(this.passoAtual()));
    if (balao.instance.indice === indice && balao.instance.total === passos.length) return;
    this.zone.run(() => {
      balao.setInput('indice', indice);
      balao.setInput('total', passos.length);
    });
  }

  private visibilidadeDoGuia(visivel: boolean): void {
    for (const overlay of [this.balao, this.recorte]) {
      if (overlay) overlay.overlayElement.style.visibility = visivel ? '' : 'hidden';
    }
  }

  private focarCampoDoDialogo(): void {
    const alvo = this.alvoDoDialogo;
    if (!alvo?.isConnected) return;
    const campo = alvo.matches('input, button, [role="combobox"]') ? alvo
      : alvo.querySelector<HTMLElement>('input:not([disabled]), [role="combobox"], button:not([disabled])');
    campo?.focus({ preventScroll: true });
  }

  /** Acompanha o layout local sem polling nem detecção de mudanças a cada rolagem. */
  private observarDialogo(alvo: HTMLElement): void {
    const dialogo = alvo.closest<HTMLElement>('mat-dialog-container, [data-tour-formulario]');
    const janela = this.documento.defaultView;
    if (!dialogo || !janela) return;
    let quadro: number | null = null;
    let alvoFocado: string | null = null;
    const atualizar = () => {
      quadro = null;
      if (!this.podeApresentarTour() || !(this.tourAtivo()?.dialogo || this.tourAtivo()?.interativo)) return;
      // A lista de opções precisa ficar livre; retomar não troca o passo nem o foco.
      if (dialogo.querySelector('[role="combobox"][aria-expanded="true"]')) {
        this.visibilidadeDoGuia(false);
        return;
      }
      const tour = this.tourAtivo();
      const passoAtual = tour.passos[this.indiceAtual()];
      const focado = alvoFocado && (passoAtual?.alvo === alvoFocado || passoAtual?.alvoAlternativo === alvoFocado) ? this.indiceAtual()
        : tour.passos.findIndex(passo => passo.alvo === alvoFocado);
      alvoFocado = null;
      let indice = focado >= 0 ? focado : this.indiceAtual();
      const elementoDoPasso = (passo: PassoDoTutorial) => this.elementoDoAlvo(passo.alvo)
        ?? (passo.alvoAlternativo ? this.elementoDoAlvo(passo.alvoAlternativo) : null);
      let atual = elementoDoPasso(tour.passos[indice]);
      // Um campo retirado pela escolha do usuário retorna ao controle anterior disponível.
      while (!atual && indice > 0) atual = elementoDoPasso(tour.passos[--indice]);
      // Ao trocar uma aba/etapa manualmente, nenhum campo anterior pode estar
      // visível. Acompanhe o primeiro campo da etapa aberta, sem clicar nela.
      if (!atual) {
        const disponivel = tour.passos.findIndex(passo => !!elementoDoPasso(passo));
        if (disponivel >= 0) {
          indice = disponivel;
          atual = elementoDoPasso(tour.passos[indice]);
        }
      }
      if (!atual) {
        this.visibilidadeDoGuia(false);
        return;
      }
      if (indice !== this.indiceAtual() || atual !== alvo) {
        this.zone.run(() => {
          this.indiceAtual.set(indice);
          this.mostrar(tour.passos[indice], atual, false);
        });
        return;
      }
      this.visibilidadeDoGuia(true);
      this.componenteLuz?.instance.atualizar();
      this.balao?.updatePosition();
      this.atualizarContador();
      // Só a reabertura explícita pelo botão de ajuda transfere o foco ao guia.
      const foco = this.focoSolicitado;
      this.focoSolicitado = undefined;
      if (foco && this.documento.activeElement === foco) {
        this.balao?.overlayElement.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
      }
    };
    const agendar = () => { quadro ??= janela.requestAnimationFrame(atualizar); };
    const aoFocar = (evento: FocusEvent) => {
      const elemento = evento.target;
      if (elemento instanceof HTMLElement) alvoFocado = elemento.closest<HTMLElement>('[data-tour]')?.dataset.tour ?? null;
      agendar();
    };
    this.zone.runOutsideAngular(() => {
      const mutacoes = janela.MutationObserver ? new janela.MutationObserver(agendar) : null;
      mutacoes?.observe(dialogo, { subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-expanded', 'aria-hidden'] });
      const dimensoes = janela.ResizeObserver ? new janela.ResizeObserver(agendar) : null;
      for (let no: HTMLElement | null = alvo; no; no = no.parentElement) {
        dimensoes?.observe(no);
        if (no === dialogo) break;
      }
      dialogo.addEventListener('scroll', agendar, true);
      dialogo.addEventListener('focusin', aoFocar);
      dialogo.addEventListener('transitionend', agendar, true);
      janela.addEventListener('resize', agendar);
      agendar();
      this.pararObservacaoDoDialogo = () => {
        if (quadro !== null) janela.cancelAnimationFrame(quadro);
        mutacoes?.disconnect();
        dimensoes?.disconnect();
        dialogo.removeEventListener('scroll', agendar, true);
        dialogo.removeEventListener('focusin', aoFocar);
        dialogo.removeEventListener('transitionend', agendar, true);
        janela.removeEventListener('resize', agendar);
      };
    });
  }

  /** A preferência do passo primeiro; as outras três entram como alternativa. */
  private posicoesPara(preferida?: PosicaoDoPasso): ConnectedPosition[] {
    const ordem = preferida ? [preferida, ...ORDEM_PADRAO.filter(item => item !== preferida)] : [...ORDEM_PADRAO];

    return ordem.map(nome => POSICOES[nome]);
  }

  private fecharOverlays(): void {
    this.pararObservacaoDoDialogo?.();
    this.pararObservacaoDoDialogo = undefined;
    if (this.alvoDoDialogo && this.balao?.overlayElement.contains(this.documento.activeElement)) {
      this.focarCampoDoDialogo();
    }
    this.balao?.dispose();
    this.balao = undefined;
    this.recorte?.dispose();
    this.recorte = undefined;
    this.componenteBalao = undefined;
    this.componenteLuz = undefined;
    this.posicionamento = undefined;
    this.alvoDoDialogo = undefined;
    this.focoSolicitado = undefined;
  }
}
